import { type FC } from "react";
import { useSWRPostData } from "@root/shared/hooks/useSWRPostData";
import { type ButtonContentsProps } from "@legacy_user_frontend/components/projects/button_contents/src/button_contents_props";
import { useSalonTourButton } from "@userFrontend/hooks/useSalonTourButton";
import { useLineConsultButton } from "@userFrontend/hooks/useLineConsultButton";
import { addQueryStrings } from "@userFrontend/utils/addQueryStrings";
import { type OsType } from "@root/shared/types/type";
import { Presentation } from "./Presentation";
import { type TopContentsProps } from "./src/top_contents";

type Props = {
  fetchUrl: string;
  fetchBody: Record<string, unknown>;
};

type FetchedData = {
  pageType: string;
  capitalizePageType: string;
  position: string;
  workCode: string;
  queryStr: string;
  isSp: boolean;
  isApp: boolean;
  // 求人・ブランド・企業詳細ではnumber、店舗詳細ではstringが返ってくるため
  clientShopId: number | string;
  gaLabel: string;
  title?: string;
  imgSrc: string;
  dummyImgPath: string;
  isPreview: boolean;
  isContractRunning: boolean;
  isShowEntrySupplementPopover: boolean;
  considerationContents: {
    considerationDisplayFlag: boolean;
    considerationNumText: string;
  };
  buttonContents: {
    keepButtonContent: {
      keepButtonUrl: string;
      keepButtonQuery: string;
      hasKeeped: boolean;
      isShowKeepBalloon: boolean;
      isShowConfirmRemoveKeepModal: boolean;
    };
    scoutButtonContent: {
      scoutDeadline?: number;
      isScouted: boolean;
      scoutedEntryUrl?: string;
      scoutClientShopId?: number;
      isScoutExtend: boolean;
      extendScoutUrl: string;
      declineScoutUrl?: string;
    };
    entryButtonContent: {
      isEnterable: boolean;
      hasEntried: boolean;
      entryUrl: string;
    };
    salonTourButtonContent: {
      isSalonTour: boolean;
      salonTourEntryUrl: string;
    };
    lineConsultButtonContent: {
      isShowLineConsultButton: boolean;
      lineUrl: string;
      fetchUrl: string;
    };
  };
  telNumberContent: {
    isShowTellButton: boolean;
    telNumber: string;
  };
  awardsLabel: {
    awardsLabelRank?: string;
    awardsLabelText?: string;
  };
  awards2022Label: {
    awardsLabelRank?: string;
    awardsLabelText?: string;
  };
  awards2023Label: {
    awardsLabelRank?: string;
    awardsLabelText?: string;
  };
  awards2024Label: {
    awardsLabelRank?: string;
    awardsLabelText?: string;
  };
  awards2025Label: {
    awardsLabelRank?: string;
    awardsLabelText?: string;
  };
  awards2026Label: {
    awardsLabelRank?: string;
    awardsLabelText?: string;
  };
  salaryContents: [
    {
      employmentTypeName: string;
      salaryTypeName: string;
      minPrice: string;
      minUnit: string;
      maxPrice: string;
      maxUnit: string;
      minSalaryParted: {
        manPart?: string;
        remainderPart?: string;
        yenPart?: string;
        unit: string;
      } | null;
      maxSalaryParted: {
        manPart?: string;
        remainderPart?: string;
        yenPart?: string;
        unit: string;
      } | null;
      isEllipsisDisplay: boolean;
    },
  ];
  images:
    | [
        {
          imageUrl: string;
          key: string;
        },
      ]
    | null;
  albumCategoryItems: TopContentsProps["albumCategoryItems"];
  isBiyo: TopContentsProps["isBiyo"];
  isWebview: TopContentsProps["isWebview"];
  osType: OsType;
};

const btnPositionControllingClassName = (position: string): string => {
  switch (position) {
    case "middle":
      return "";
    case "top":
      return "js-footer-fix__first-point";
    default:
      return "js-footer-fix__last-point";
  }
};

const getFirstValidAwardLabel = ({
  awards2026Label,
  awards2025Label,
  awards2024Label,
  awards2023Label,
  awards2022Label,
  awardsLabel,
}: {
  awards2026Label: ButtonContentsProps["awards2026Label"];
  awards2025Label: ButtonContentsProps["awards2025Label"];
  awards2024Label: ButtonContentsProps["awards2024Label"];
  awards2023Label: ButtonContentsProps["awards2023Label"];
  awards2022Label: ButtonContentsProps["awards2022Label"];
  awardsLabel: ButtonContentsProps["awardsLabel"];
}): ButtonContentsProps["awardsLabel"] | undefined => {
  const allLabels = [
    awards2026Label,
    awards2025Label,
    awards2024Label,
    awards2023Label,
    awards2022Label,
    awardsLabel,
  ];
  return allLabels.find((label) => Boolean(label?.awardsLabelRank && label?.awardsLabelText));
};

export const ButtonContents: FC<Props> = ({ fetchUrl, fetchBody }) => {
  const { handleClickSalonTourButton } = useSalonTourButton();
  const { handleClickLineConsultButton } = useLineConsultButton();
  const data = useSWRPostData<{ data: FetchedData }>(fetchUrl, fetchBody);

  // ロード中と保存済みの検索条件がない場合は何も表示しない
  if (!data) return null;

  // サーバ側で例外が起きると HTTP 200 のまま data が null で返ることがあるため、
  // 期待する形でなければ何も表示しない
  if (!data.data?.buttonContents) return null;

  const {
    pageType,
    capitalizePageType,
    position,
    workCode,
    queryStr,
    isSp,
    isApp,
    clientShopId,
    gaLabel,
    title,
    imgSrc,
    dummyImgPath,
    isPreview,
    isContractRunning,
    isShowEntrySupplementPopover,
    considerationContents,
    buttonContents,
    telNumberContent,
    awardsLabel,
    awards2022Label,
    awards2023Label,
    awards2024Label,
    awards2025Label,
    awards2026Label,
    salaryContents,
    images,
    albumCategoryItems,
    isBiyo,
    isWebview,
    osType,
  } = data.data;
  const awardsLabelContent = getFirstValidAwardLabel({
    awards2026Label,
    awards2025Label,
    awards2024Label,
    awards2023Label,
    awards2022Label,
    awardsLabel,
  });
  const scoutGaLabel = `${workCode}_${buttonContents.scoutButtonContent.scoutClientShopId}`;
  const dataStyleColumn =
    (pageType !== "detail" && !imgSrc) || position === "middle" || position === "bottom" ? 1 : 2;

  // 表示条件のロジック
  const isSalonTourButtonVisible =
    buttonContents.salonTourButtonContent.isSalonTour &&
    !buttonContents.scoutButtonContent.isScouted &&
    !buttonContents.entryButtonContent.hasEntried;
  const isTelButtonVisible =
    isSp &&
    telNumberContent.isShowTellButton &&
    buttonContents.entryButtonContent.isEnterable &&
    !buttonContents.entryButtonContent.hasEntried;

  // SalonTourButtonのonClickハンドラー
  const handleSalonTourButtonClick = () => {
    if (!isPreview) {
      handleClickSalonTourButton({
        isApp,
        workCode,
        isScouted: buttonContents.scoutButtonContent.isScouted,
        clientShopId,
        salonTourEntryUrl: isApp
          ? buttonContents.salonTourButtonContent.salonTourEntryUrl
          : addQueryStrings(buttonContents.salonTourButtonContent.salonTourEntryUrl, queryStr),
      });
    }
  };

  // LineConsultButtonのonClickハンドラー
  const handleLineConsultButtonClick = () => {
    if (!isPreview) {
      handleClickLineConsultButton({
        workCode,
        lineUrl: buttonContents.lineConsultButtonContent.lineUrl,
        createHistoryUrl: buttonContents.lineConsultButtonContent.fetchUrl,
        clientShopId,
      });
    }
  };

  return (
    <Presentation
      pageType={pageType}
      capitalizePageType={capitalizePageType}
      position={position}
      workCode={workCode}
      queryStr={queryStr}
      isSp={isSp}
      isApp={isApp}
      isBiyo={isBiyo}
      isWebview={isWebview}
      osType={osType}
      clientShopId={clientShopId}
      gaLabel={gaLabel}
      title={title}
      imgSrc={imgSrc}
      dummyImgPath={dummyImgPath}
      isPreview={isPreview}
      isContractRunning={isContractRunning}
      isShowEntrySupplementPopover={isShowEntrySupplementPopover}
      considerationContents={considerationContents}
      buttonContents={buttonContents}
      telNumberContent={telNumberContent}
      awardsLabelContent={awardsLabelContent}
      salaryContents={salaryContents}
      images={images}
      albumCategoryItems={albumCategoryItems}
      btnPositionControllingClassName={btnPositionControllingClassName(position)}
      handleSalonTourButtonClick={handleSalonTourButtonClick}
      handleLineConsultButtonClick={handleLineConsultButtonClick}
      isSalonTourButtonVisible={isSalonTourButtonVisible}
      isTelButtonVisible={isTelButtonVisible}
      scoutGaLabel={scoutGaLabel}
      dataStyleColumn={dataStyleColumn}
      isShowInquiryMessageLabel={pageType === "detail" && position === "top"}
    />
  );
};
